/**
 * Shared, admin-editable price cards used on every pricing surface
 * (homepage, /pricing, upgrade modal, in-app paywall).
 * Source of truth: GET /api/public/pricing-cards (resolves the `pricing_cards`
 * site setting + linked Stripe plan). No hard-coded plan copy lives here.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PricingCards = ({ onSelect, paidOnly = false, compact = false, className = "" }) => {
  const [cards, setCards] = useState([]);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/public/pricing-cards`)
      .then((r) => { if (mounted) setCards(r.data?.cards || []); })
      .catch(() => { if (mounted) setCards([]); });
    return () => { mounted = false; };
  }, []);

  const shown = paidOnly ? cards.filter((c) => c.plan_key) : cards;
  if (shown.length === 0) return null;

  const n = shown.length;
  const cols = n <= 1 ? "" : n === 2 ? "sm:grid-cols-2" : n === 3 ? "md:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4";

  return (
    <div className={`grid grid-cols-1 ${cols} gap-5 items-stretch pt-3 ${className}`} data-testid="pricing-cards">
      {shown.map((card) => (
        <div
          key={card.id}
          data-testid={`pricing-card-${card.id}`}
          className={`relative rounded-2xl bg-card flex flex-col ${compact ? "p-5" : "p-7"} ${
            card.highlight ? "border-2 border-primary" : "border border-border"
          }`}
        >
          {card.highlight && (
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold whitespace-nowrap">
              Best value
            </span>
          )}
          <h3 className="font-semibold" data-testid={`pricing-card-title-${card.id}`}>{card.title}</h3>
          <div className="mt-3 flex items-baseline gap-1">
            <span className={compact ? "text-3xl font-bold" : "text-4xl font-bold"} data-testid={`pricing-card-price-${card.id}`}>
              {card.price_text}
            </span>
            {card.period_text && <span className="text-sm text-muted-foreground">{card.period_text}</span>}
          </div>
          <ul className="mt-6 space-y-2.5 text-sm flex-1">
            {(card.features || []).map((f, i) => (
              <li key={i} className={`flex items-start gap-2 ${/plus:$/.test(f) ? "font-medium" : ""}`}>
                <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" /> <span>{f}</span>
              </li>
            ))}
          </ul>
          <Button
            data-testid={`pricing-card-cta-${card.id}`}
            variant={card.plan_key ? "default" : "outline"}
            className={`mt-7 rounded-full w-full ${card.highlight ? "glow-primary" : ""}`}
            onClick={() => onSelect?.(card)}
          >
            {card.button_label}
          </Button>
        </div>
      ))}
    </div>
  );
};

export default PricingCards;
